<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import usePhoneNumber from '@Src/use/usePhoneNumber';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useDisplayName } from '@Shared';
import type {
    WiringInstructions,
    Address,
} from '@Types/WiringInstrucions.interface';
import { formatAddressLine, formatCityStateZip } from '@Use/useAddressFormatter';
import { isEmdSendType } from '@Enums/sendType.enum';

import ExpandCollapseToggle from '@Components/ExpandCollapseToggle.vue';
import CoverageBanner from '@Components/CoverageBanner.vue';
import KeyValueDetails, {
    type KeyValueSection,
} from '@Components/KeyValueDetails.vue';
import { getPdfUrlApi } from '@Api/pdf.api';
import { trackPdfDownloaded } from '@Analytics/request.mixpanel';

import bankInfoIllustration from '@Assets/images/bank-info-illustration.svg?inline';

withDefaults(defineProps<{
    isDashboardContext: boolean;
    errorMessage?: string | null;
    showTitle?: boolean;
}>(), {
    showTitle: true,
});

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const { rawPhone, formattedPhone } = usePhoneNumber(
    () => companyStore.company,
    () => locationStore.location,
);
const requestSessionStore = useRequestSessionStore();
const { hasRouteInFlow, navigateToRoute } = useFlowNavigation();
const requestDetailsStore = useRequestDetailsStore();

const pdfUrl = ref<string | null>(null);
const pdfLoadFailed = ref(false);
const pdfLoading = ref(false);

const isPdfDownloadReady = computed(
    () => Boolean(pdfUrl.value) && !pdfLoading.value,
);

const loadPdfUrl = async () => {
    pdfUrl.value = null;
    pdfLoading.value = true;
    pdfLoadFailed.value = false;
    try {
        pdfUrl.value = await getPdfUrlApi();
    } catch {
        pdfLoadFailed.value = true;
        pdfUrl.value = null;
    } finally {
        pdfLoading.value = false;
    }
};

const openPdf = () => {
    if (!isPdfDownloadReady.value || !pdfUrl.value) return;
    trackPdfDownloaded();
    const opened = window.open(pdfUrl.value, '_blank', 'noopener,noreferrer');
    if (opened) {
        opened.opener = null;
    }
};

const { displayName: companyName } = useDisplayName(
    () => companyStore.company!,
    () => locationStore.location,
);

const mppPurchased = computed(() => requestDetailsStore.isMppPurchased);
const requestDetail = computed(() => requestDetailsStore.requestDetails);

// DS-3309: when the request purpose is an earnest money deposit (wire-instructions-only
// EMD), make the detail page clearly state the instructions are for the buyer's EMD
// payment instead of generic/closing-funds wording.
const isEmd = computed(() => isEmdSendType(requestDetail.value?.sendType));

const getCreditToAccountAddress = (
    accountAddress: Address | null | undefined,
    locationAddress: Address | null | undefined,
): Address | null | undefined => {
    if (accountAddress?.address1?.trim()) return accountAddress;
    return locationAddress ?? accountAddress;
};

const buildWiringSections = (wi: WiringInstructions): KeyValueSection[] => {
    const finalCreditToAddress = getCreditToAccountAddress(
        wi.furtherCreditTo?.accountAddress,
        wi.locationAddress,
    );
    const creditToAddress = getCreditToAccountAddress(
        wi.creditTo?.accountAddress,
        wi.locationAddress,
    );
    const finalCreditToSection: KeyValueSection | undefined = wi.furtherCreditTo?.accountName
        ? {
            title: 'Final credit funds to',
            rows: [
                {
                    type: 'stacked',
                    valueWeight: 'normal',
                    values: [
                        wi.furtherCreditTo?.accountName,
                        formatAddressLine(finalCreditToAddress?.address1) || undefined,
                        formatCityStateZip(finalCreditToAddress) || undefined,
                    ],
                },
                {
                    type: 'labeled',
                    label: 'Account number',
                    value: wi.furtherCreditTo?.accountNumber,
                },
            ],
        }
        : undefined;

    return [
        {
            title: 'Bank information',
            rows: [
                { type: 'plain', value: wi.accountHolderName },
                { type: 'plain', value: wi.bankName },
                {
                    type: 'labeled',
                    label: 'Account number',
                    value: wi.accountNumber,
                },
                {
                    type: 'labeled',
                    label: 'Routing number',
                    value: wi.routingNumber,
                },
            ],
        },
        ...(finalCreditToSection ? [finalCreditToSection] : []),
        {
            title: 'Credit funds to',
            rows: [
                {
                    type: 'stacked',
                    valueWeight: 'normal',
                    values: [
                        wi.creditTo?.accountName,
                        formatAddressLine(creditToAddress?.address1) || undefined,
                        formatCityStateZip(creditToAddress) || undefined,
                    ],
                },
                {
                    type: 'labeled',
                    label: 'Account number',
                    value: wi.creditTo?.accountNumber,
                },
            ],
        },
        {
            title: 'Intermediary bank',
            rows: [
                {
                    type: 'stacked',
                    valueWeight: 'normal',
                    values: [
                        wi.correspondentBank?.name,
                        formatAddressLine(wi.correspondentBank?.address?.address1) || undefined,
                        formatCityStateZip(wi.correspondentBank?.address) || undefined,
                    ],
                },
                {
                    type: 'labeled',
                    label: 'Wire routing number',
                    value: wi.correspondentBank?.routingNumber,
                },
            ],
        },
        {
            title: 'Beneficiary bank',
            rows: [
                {
                    type: 'stacked',
                    valueWeight: 'normal',
                    values: [
                        wi.beneficiaryBank?.name,
                        formatAddressLine(wi.beneficiaryBank?.address?.address1) || undefined,
                        formatCityStateZip(wi.beneficiaryBank?.address) || undefined,
                    ],
                },
                {
                    type: 'labeled',
                    label: 'ABA routing number',
                    value: wi.beneficiaryBank?.routingNumber,
                },
            ],
        },
    ];
};

const wiringSections = computed<KeyValueSection[]>(() =>
    requestDetail.value?.wiringInstructions
        ? buildWiringSections(requestDetail.value.wiringInstructions)
        : [],
);

const navigateToMpp = () => {
    const target =
        requestSessionStore.selectedMppPolicy && hasRouteInFlow('mpp-purchase-decision')
            ? 'mpp-purchase-decision'
            : 'mpp-transfer-amount';
    navigateToRoute(target);
};

onMounted(async () => {
    await loadPdfUrl();
});
</script>

<template>
    <app-form-content-layout>
        <p
            v-if="errorMessage"
            class="wire-instructions__error"
            role="alert"
        >
            {{ errorMessage }}
        </p>

        <div
            v-if="requestDetail && !isDashboardContext"
            class="wire-instructions__illustration"
        >
            <img
                :src="bankInfoIllustration"
                alt="Bank info illustration"
                class="wire-instructions__illustration-img"
            />
        </div>

        <h1
            v-if="showTitle && requestDetail"
            class="wire-instructions__headline"
            bottomOffset="1rem"
        >
            Your wire instructions
        </h1>

        <p
            v-if="requestDetail && isEmd"
            class="wire-instructions__purpose"
            data-id="wire-instructions-emd-label"
        >
            These instructions are for your earnest money deposit (EMD).
        </p>

        <p v-if="requestDetail" class="wire-instructions__description">
            <strong>Download the wire instructions packet.</strong>
            This packet contains the details you need to complete your
            <template v-if="isEmd">earnest money deposit</template>
            <template v-else>transaction</template>.
            {{ companyName }} will provide you with the final transfer amount.
        </p>

        <button
            v-if="requestDetail"
            type="button"
            class="wire-instructions__document-card"
            :class="{ 'wire-instructions__document-card--disabled': !isPdfDownloadReady }"
            :disabled="!isPdfDownloadReady"
            data-id="wire-instructions-download"
            @click="openPdf"
        >
            <div class="wire-instructions__document-icon-container">
                <app-icon
                    icon="cd-ic-document"
                    class="wire-instructions__document-icon"
                />
            </div>
            <div class="wire-instructions__document-content">
                <span class="wire-instructions__document-title">
                    Wire Instructions Packet
                </span>
                <span v-if="pdfLoading" class="wire-instructions__document-status">
                    Loading...
                </span>
            </div>
            <app-icon
                v-if="isPdfDownloadReady"
                icon="cd-ic-download"
                class="wire-instructions__download-icon"
            />
        </button>

        <app-banner-notice
            v-if="requestDetail && pdfLoadFailed"
            type="alert"
            class="wire-instructions__pdf-error"
        >
            Packet failed to load.
            <app-link
                emphasized
                type="internal"
                data-id="wire-instructions-retry"
                @onClick="loadPdfUrl"
            >
                Try again
            </app-link>
        </app-banner-notice>

        <app-collapse
            v-if="requestDetail && requestDetail.wiringInstructions"
            class="wire-instructions__collapsible"
            initial-state="closed"
        >
            <template #trigger="slotProps">
                <expand-collapse-toggle
                    :is-open="slotProps?.isOpen"
                    open-label="Hide wire information"
                    closed-label="Show wire information"
                    data-id="wire-instructions-toggle"
                />
            </template>
            <key-value-details :sections="wiringSections">
                <app-callout
                    type="alert"
                    title="THESE INSTRUCTIONS NEVER CHANGE"
                    class="wire-instructions__callout"
                    emphasized
                >
                    If you receive a message with new or revised
                    instructions please call: {{ companyName }}
                    <template v-if="rawPhone">
                        - {{ formattedPhone }}
                    </template>
                </app-callout>
            </key-value-details>
        </app-collapse>

        <div v-if="requestDetail" class="wire-instructions__disclaimer">
            <div class="wire-instructions__disclaimer-header">
                <span class="wire-instructions__disclaimer-title">
                    QUESTIONS ABOUT THIS TRANSFER?
                </span>
            </div>
            <p class="wire-instructions__disclaimer-body">
                Your Wire Instructions Packet includes guidance about how
                to set up a wire transfer. If you still have questions or
                need more help, contact {{ companyName }}.
            </p>
        </div>

        <coverage-banner v-if="requestDetail && mppPurchased" />

        <app-link
            v-if="hasRouteInFlow('mpp-transfer-amount') && !mppPurchased"
            emphasized
            type="internal"
            class="wire-instructions__mpp-cta"
            data-id="wire-instructions-mpp-cta"
            @onClick="navigateToMpp"
        >
            Interested in our Money Protection Plan?
        </app-link>
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.wire-instructions {
    &__illustration {
        display: flex;
        justify-content: center;
        align-self: stretch;
    }

    &__illustration-img {
        width: 150px;
        height: auto;
    }

    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__purpose {
        font-size: 0.9rem;
        font-weight: 700;
        line-height: 150%;
        color: $theme-primary;
        margin: 0;
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }

    &__document-card {
        display: flex;
        align-items: center;
        align-self: stretch;
        gap: 1rem;
        padding: 1rem;
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        cursor: pointer;
        text-align: left;
        // Buttons don't inherit the page font-family by default.
        font-family: inherit;

        &:hover:not(:disabled) {
            border-color: $theme-primary;
        }

        &--disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    &__document-status {
        font-size: 0.75rem;
        color: $black-med;
    }

    &__pdf-error {
        align-self: stretch;
    }

    &__document-icon-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        background-color: var(--ch-primary-dark);
        border-radius: 50%;
        flex-shrink: 0;
    }

    &__document-icon {
        font-size: 1.5rem;
        color: $white;
    }

    &__document-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    &__document-title {
        font-size: 1rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
    }

    &__download-icon {
        flex-shrink: 0;
        width: 1.5rem;
        height: 1.5rem;
        font-size: 1.5rem;
        color: $theme-primary;
    }

    &__collapsible {
        display: flex;
        flex-direction: column;

        :deep(.trigger-indicator) {
            display: none;
        }
    }

    &__callout {
        background-color: $white;

        &:deep(.app-callout__content) {
            color: $red;
            font-weight: 700;
            line-height: 150%;
        }
    }

    &__disclaimer {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        gap: 0.5rem;
        padding: 1rem;
        background: $white;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
    }

    &__disclaimer-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    &__disclaimer-title {
        font-size: 0.9rem;
        font-weight: 700;
        color: $black-high;
    }

    &__disclaimer-body {
        font-size: 0.9rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__mpp-cta {
        align-self: stretch;
        padding: 0.5625rem 0.75rem;
        font-size: 0.9rem;
        text-align: center;
    }
}
</style>
